import type { AnalyticsEvent, Order, OrderItem, FunnelStep, SearchInsight } from '../types';

const PROJECT_ID = '436481';

// Route API calls through a same-origin proxy so the browser never calls the
// analytics REST API cross-origin (it does not send CORS headers).
//  - Dev:  Vite proxies /analytics-api (vite.config.ts)
//  - Prod: Cloudflare Pages Function proxies /api/analytics
const DEFAULT_BASE = import.meta.env.DEV ? '/analytics-api' : '/api/analytics';

// Events we track and cache locally (custom commerce events + pageviews for
// session timing). Autocapture/web-vitals noise is never stored.
export const TRACKED_EVENTS = [
  'viewed_product_page',
  'add_to_cart',
  'cart_viewed',
  'checkout_started',
  'checkout_snapshot',
  'checkout_success',
  'search_performed',
  '$pageview',
] as const;

// Sanitize defensively: trim whitespace/newlines and strip surrounding quotes,
// the most common reasons an otherwise-valid .env value is "ignored".
function readApiKey(): string {
  const raw = import.meta.env.VITE_ANALYTICS_API_KEY;
  if (!raw) return '';
  return String(raw).trim().replace(/^["']+|["']+$/g, '').trim();
}
const API_KEY = readApiKey();

interface AnalyticsConfig {
  apiKey: string;
  projectId: string;
  baseUrl?: string;
}

class AnalyticsClient {
  private apiKey: string;
  private projectId: string;
  private baseUrl: string;

  constructor(config?: Partial<AnalyticsConfig>) {
    this.apiKey = config?.apiKey || API_KEY;
    this.projectId = config?.projectId || PROJECT_ID;
    this.baseUrl = config?.baseUrl || DEFAULT_BASE;
  }

  get hasApiKey(): boolean {
    return this.apiKey.length > 0;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    if (!this.apiKey) {
      throw new Error('Analytics API key not configured. Set VITE_ANALYTICS_API_KEY in .env, then restart the dev server (Vite only reads .env at startup).');
    }

    if (this.apiKey.startsWith('phc_')) {
      throw new Error('VITE_ANALYTICS_API_KEY is a project key (phc_…), which cannot read the REST API. Use a personal API key (phx_…) from your analytics account settings instead.');
    }

    const url = `${this.baseUrl}${endpoint}`;
    let response: Response;
    try {
      response = await fetch(url, {
        ...options,
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });
    } catch {
      throw new Error('Could not reach the analytics API. Check your network connection.');
    }

    if (response.status === 401 || response.status === 403) {
      throw new Error('Analytics API rejected the key (401/403). Make sure VITE_ANALYTICS_API_KEY is a personal API key (phx_…), not a project key.');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || `Analytics API error: ${response.status}`);
    }

    return response.json();
  }

  private async query(sql: string): Promise<any> {
    return this.request(`/projects/${this.projectId}/query/`, {
      method: 'POST',
      body: JSON.stringify({ query: { kind: 'HogQLQuery', query: sql } }),
    });
  }

  /**
   * Fetch tracked events newer than `after` (ISO string, exclusive-ish).
   * One HogQL query returns every tracked event type at once.
   */
  async fetchEventsSince(after?: string, before?: string): Promise<AnalyticsEvent[]> {
    const eventList = TRACKED_EVENTS.map(e => `'${e}'`).join(', ');
    const conditions = [`event IN (${eventList})`];
    if (after) conditions.push(`timestamp > toDateTime('${after}')`);
    if (before) conditions.push(`timestamp < toDateTime('${before}')`);

    const sql = `
      SELECT uuid, event, distinct_id, timestamp, properties
      FROM events
      WHERE ${conditions.join(' AND ')}
      ORDER BY timestamp ASC
      LIMIT 10000
    `;

    const res = await this.query(sql);
    const rows: any[][] = res.results || [];

    return rows.map(([uuid, event, distinctId, timestamp, properties]) => ({
      id: uuid,
      event,
      distinct_id: String(distinctId),
      timestamp: typeof timestamp === 'string' ? timestamp : new Date(timestamp).toISOString(),
      properties: typeof properties === 'string' ? JSON.parse(properties) : (properties || {}),
    }));
  }
}

/* ── Pure mapping functions (work on the locally cached event store) ── */

// Friendly display labels for payment method codes.
export const PAYMENT_METHOD_LABELS: Record<string, string> = {
  checkmo: 'Terms',
};

export function paymentLabel(code: string | undefined | null): string | undefined {
  if (!code) return undefined;
  return PAYMENT_METHOD_LABELS[code] ?? code;
}

/**
 * Apply admin exclusion rules and deleted-event ids to the raw event store.
 * Rules can match a user id (distinct_id / customer_id) or any event property.
 */
export function applyExclusions(
  events: AnalyticsEvent[],
  exclusions: { kind: 'user' | 'property'; key?: string; value: string; op?: 'equals' | 'contains' }[],
  deletedIds: string[] = []
): AnalyticsEvent[] {
  if (exclusions.length === 0 && deletedIds.length === 0) return events;
  const deleted = new Set(deletedIds);

  const matches = (actual: unknown, rule: { value: string; op?: 'equals' | 'contains' }): boolean => {
    if (actual == null) return false;
    const s = String(actual);
    return rule.op === 'contains'
      ? s.toLowerCase().includes(rule.value.toLowerCase())
      : s === rule.value;
  };

  return events.filter(e => {
    if (deleted.has(e.id)) return false;
    for (const rule of exclusions) {
      if (rule.kind === 'user') {
        const customerId = e.properties?.customer_id != null ? String(e.properties.customer_id) : null;
        if (matches(e.distinct_id, rule) || matches(customerId, rule)) return false;
      } else if (rule.kind === 'property' && rule.key) {
        if (matches(e.properties?.[rule.key], rule)) return false;
      }
    }
    return true;
  });
}

function toOrderItem(raw: any): OrderItem {
  return {
    partNumber: raw.part_number || raw.sku || 'UNKNOWN',
    name: raw.item_name || raw.part_number || 'Unknown item',
    sku: raw.sku,
    quantity: Number(raw.quantity) || 1,
    unitPrice: Number(raw.unit_price) || 0,
    listPrice: raw.list_price != null ? Number(raw.list_price) : undefined,
    discountPercent: raw.discount_percent != null ? Number(raw.discount_percent) : undefined,
    sellerName: raw.seller_name,
    businessUnit: raw.business_unit,
    category: raw.category,
  };
}

function customerLabel(props: Record<string, any>, distinctId: string, aliases: Record<string, string>): string {
  const id = props.customer_id ?? distinctId;
  const alias = aliases[String(id)];
  if (alias) return alias;
  return id != null ? `Customer ${id}` : 'Unknown';
}

/** Map cached analytics events into orders (completed + abandoned). */
export function mapEventsToOrders(events: AnalyticsEvent[], aliases: Record<string, string> = {}): Order[] {
  const orders: Order[] = [];

  // Group journey events by quote_id so we can find session starts and
  // detect carts that never converted.
  const byQuote = new Map<string, AnalyticsEvent[]>();
  const successEvents: AnalyticsEvent[] = [];

  for (const e of events) {
    if (e.event === 'checkout_success') {
      successEvents.push(e);
    }
    const qid = e.properties?.quote_id;
    if (qid != null) {
      const key = String(qid);
      if (!byQuote.has(key)) byQuote.set(key, []);
      byQuote.get(key)!.push(e);
    }
  }

  const convertedQuotes = new Set(
    successEvents.map(e => String(e.properties?.quote_id ?? '')).filter(Boolean)
  );

  // Completed orders
  for (const e of successEvents) {
    const p = e.properties || {};
    const items: OrderItem[] = Array.isArray(p.items) ? p.items.map(toOrderItem) : [];
    const qid = p.quote_id != null ? String(p.quote_id) : undefined;
    const journey = qid ? (byQuote.get(qid) || []) : [];
    const journeyStart = journey.length
      ? journey.reduce((min, ev) => (ev.timestamp < min ? ev.timestamp : min), journey[0].timestamp)
      : e.timestamp;
    const sellers = [...new Set(items.map(i => i.sellerName).filter(Boolean))] as string[];

    orders.push({
      id: p.order_id != null ? `order_${p.order_id}` : `order_${e.id}`,
      incrementId: p.order_increment_id,
      poNumber: p.po_number ?? p.order_increment_id,
      customerName: customerLabel(p, e.distinct_id, aliases),
      customerEmail: p.customer_type ? `${p.customer_type} · ID ${p.customer_id ?? e.distinct_id}` : `ID ${e.distinct_id}`,
      partNumbers: items.map(i => i.partNumber),
      items,
      total: Number(p.order_grand_total) || 0,
      subtotal: p.order_subtotal != null ? Number(p.order_subtotal) : undefined,
      shippingTotal: p.order_shipping_total != null ? Number(p.order_shipping_total) : undefined,
      taxTotal: p.order_tax_total != null ? Number(p.order_tax_total) : undefined,
      discountTotal: p.order_discount_total != null ? Number(p.order_discount_total) : undefined,
      currency: p.currency || 'USD',
      paymentMethod: paymentLabel(p.payment_method),
      shippingMethodLabel: p.shipping_method_label,
      sellerNames: sellers,
      quoteId: p.quote_id,
      status: 'unknown',
      createdAt: e.timestamp,
      updatedAt: e.timestamp,
      sessionStart: journeyStart,
      checkoutSuccess: e.timestamp,
      abandoned: false,
      source: 'live',
      notes: '',
    });
  }

  // Abandoned carts: quotes that reached cart/checkout but never converted.
  for (const [qid, journey] of byQuote) {
    if (convertedQuotes.has(qid)) continue;
    const hasIntent = journey.some(ev =>
      ev.event === 'checkout_started' || ev.event === 'add_to_cart' || ev.event === 'cart_viewed'
    );
    if (!hasIntent) continue;

    const latest = journey.reduce((a, b) => (a.timestamp > b.timestamp ? a : b));
    const p = latest.properties || {};
    const items: OrderItem[] = Array.isArray(p.items) ? p.items.map(toOrderItem) : [];

    orders.push({
      id: `abandoned_${qid}`,
      customerName: customerLabel(p, latest.distinct_id, aliases),
      customerEmail: p.customer_type ? `${p.customer_type} · ID ${p.customer_id ?? latest.distinct_id}` : `ID ${latest.distinct_id}`,
      partNumbers: items.map(i => i.partNumber),
      items,
      total: Number(p.cart_grand_total) || 0,
      subtotal: p.cart_subtotal != null ? Number(p.cart_subtotal) : undefined,
      discountTotal: p.cart_discount_total != null ? Number(p.cart_discount_total) : undefined,
      currency: p.currency || 'USD',
      sellerNames: [...new Set(items.map(i => i.sellerName).filter(Boolean))] as string[],
      quoteId: p.quote_id,
      status: 'unknown',
      createdAt: journey[0].timestamp,
      updatedAt: latest.timestamp,
      sessionStart: journey[0].timestamp,
      abandoned: true,
      source: 'live',
      notes: '',
    });
  }

  return orders.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

/** Conversion funnel across the commerce journey. */
export function buildFunnel(events: AnalyticsEvent[]): FunnelStep[] {
  const count = (name: string) => events.filter(e => e.event === name).length;
  const distinctQuotes = (names: string[]) =>
    new Set(
      events
        .filter(e => names.includes(e.event) && e.properties?.quote_id != null)
        .map(e => String(e.properties.quote_id))
    ).size;

  const steps: Array<[string, string, number]> = [
    ['viewed_product_page', 'Product Views', count('viewed_product_page')],
    ['add_to_cart', 'Add to Cart', distinctQuotes(['add_to_cart'])],
    ['cart_viewed', 'Cart Viewed', distinctQuotes(['cart_viewed', 'add_to_cart'])],
    ['checkout_started', 'Checkout Started', distinctQuotes(['checkout_started', 'checkout_snapshot', 'checkout_success'])],
    ['checkout_success', 'Orders Placed', distinctQuotes(['checkout_success'])],
  ];

  return steps.map(([key, label, value], i) => ({
    key,
    label,
    count: value,
    conversionFromPrevious: i > 0 && steps[i - 1][2] > 0 ? (value / steps[i - 1][2]) * 100 : null,
  }));
}

/** Search behavior: zero-result queries are catalog gaps worth surfacing. */
export function buildSearchInsights(events: AnalyticsEvent[]): SearchInsight[] {
  const byQuery = new Map<string, SearchInsight>();

  for (const e of events) {
    if (e.event !== 'search_performed') continue;
    const p = e.properties || {};
    const query = String(p.search_query ?? '').trim();
    if (!query) continue;
    const type = String(p.search_type || 'search');
    const key = `${query}::${type}`;
    const results = Number(p.results_count) || 0;

    const existing = byQuery.get(key);
    if (existing) {
      existing.searches += 1;
      existing.resultsCount = Math.max(existing.resultsCount, results);
      existing.zeroResults = existing.resultsCount === 0;
    } else {
      byQuery.set(key, {
        query,
        type,
        searches: 1,
        resultsCount: results,
        zeroResults: results === 0,
      });
    }
  }

  return [...byQuery.values()].sort((a, b) => b.searches - a.searches);
}

export const analytics = new AnalyticsClient();
export default AnalyticsClient;
